import React from 'react'
import { useImmerReducer } from 'use-immer'
import { AreaSelection, EditorModes, AreaTools } from 'models/editor'
import { Feature } from 'models/map'
import { GeometryTypes, Layer } from 'models/layer'
import deepmerge from 'deepmerge'

export enum MapEditorWizards {
  CHANGE_PROJECT_AREA = 'CHANGE_PROJECT_AREA',
  PROJECT_CREATE = 'PROJECT_CREATE',
  ADD_FEATURE = 'ADD_FEATURE',
  TRAFFIC_TOOL = 'TRAFFIC_TOOL',
  COMPARE_AREA = 'COMPARE_AREA',
  MOBILITY_REPORT_TOOL = 'MOBILITY_REPORT_TOOL',
  PREDICTIVE_REPORT_TOOL = 'PREDICTIVE_REPORT_TOOL',
  CUSTOM_REPORT_TOOL = 'CUSTOM_REPORT_TOOL',
  KEYWORDS_REPORT_TOOL = 'KEYWORDS_REPORT_TOOL',
}

export type Wizard = MapEditorWizards | null

export enum Actions {
  editLayer = 'layer/edit',
  setWizard = 'project/set-wizard',
  cancelWizard = 'project/cancel',
  setConfirm = 'feature/change-confirm',
  updateFeature = 'feature/update',
  featureCreated = 'feature/create',
  selectTool = 'editor/select-tool',
}

type State = {
  wizard: Wizard
  editingLayer: Layer | null
  areaTool: AreaSelection
  confirmed: boolean
  editor: {
    mode: EditorModes
    feature: Feature | null
  }
}

const initialState: State = {
  wizard: null,
  editingLayer: null,
  areaTool: null,
  confirmed: false,
  editor: {
    mode: EditorModes.READ_ONLY,
    feature: null,
  },
}

const MapEditorStateContext = React.createContext<State>(initialState)
const MapEditorDispatchContext = React.createContext<any | undefined>(undefined)

function reducer(draft: State, action: any) {
  switch (action.type) {
    case Actions.cancelWizard:
    case Actions.featureCreated:
      return initialState

    case Actions.updateFeature:
      draft.editor.feature = action.payload
      draft.editor.mode = [EditorModes.DRAW_POINT, EditorModes.DRAW_PATH].includes(
        draft.editor.mode,
      )
        ? draft.editor.mode
        : EditorModes.EDITING

      return draft

    case Actions.setConfirm:
      draft.confirmed = action.payload
      return draft

    case Actions.editLayer:
      draft.editingLayer = action.payload
      draft.wizard = MapEditorWizards.ADD_FEATURE
      draft.editor.feature = null

      if (action.payload.geometryType === GeometryTypes.Point) {
        draft.editor.mode = EditorModes.DRAW_POINT
        draft.areaTool = AreaTools.POINT
      } else {
        draft.editor.mode = EditorModes.DRAW_POLYGON
        draft.areaTool = AreaTools.POLYGON
      }

      return draft

    case Actions.selectTool:
      draft.editor.mode = EditorModes.READ_ONLY
      draft.editor.feature = null
      draft.areaTool = action.payload

      if (action.payload === AreaTools.POINT) draft.editor.mode = EditorModes.DRAW_POINT
      if (action.payload === AreaTools.POINT2) draft.editor.mode = EditorModes.DRAW_POINT
      if (action.payload === AreaTools.POINT3) draft.editor.mode = EditorModes.SELECT
      if (action.payload === AreaTools.POLYGON) draft.editor.mode = EditorModes.DRAW_POLYGON
      if (action.payload === AreaTools.RADIUS) draft.editor.mode = EditorModes.DRAW_CIRCLE
      if (action.payload === AreaTools.TRAFFIC_TOOL) draft.editor.mode = EditorModes.DRAW_POINT
      if (action.payload === AreaTools.MEASURE) draft.editor.mode = EditorModes.DRAW_PATH
      if (action.payload === AreaTools.POINT_PONDERA) draft.editor.mode = EditorModes.DRAW_POINT
      if (
        [
          AreaTools.MOBILITY_REPORT_TOOL,
          AreaTools.CUSTOM_REPORT_TOOL,
          AreaTools.KEYWORDS_REPORT_TOOL,
        ].includes(action.payload)
      ) {
        draft.editor.mode = EditorModes.DRAW_POINT
      }
      if (
        [
          AreaTools.PREDICTIVE_REPORT_TOOL,
          AreaTools.CUSTOM_REPORT_TOOL,
          AreaTools.KEYWORDS_REPORT_TOOL,
        ].includes(action.payload)
      ) {
        draft.editor.mode = EditorModes.DRAW_POINT
      }
      if (action.payload === AreaTools.REGION) draft.confirmed = true
      if (action.payload === AreaTools.FILE_UPLOAD) draft.confirmed = true
      return draft

    case Actions.setWizard:
      draft.wizard = action.payload
      return draft
  }
}

export const MapEditorProvider: React.FC<{ defaultValue?: Partial<State> }> = ({
  children,
  defaultValue = {},
}) => {
  const mapEditorState = deepmerge(initialState, defaultValue)
  const [state, dispatch] = useImmerReducer<State>(reducer, mapEditorState)

  return (
    <MapEditorStateContext.Provider value={state}>
      <MapEditorDispatchContext.Provider value={dispatch}>
        {children}
      </MapEditorDispatchContext.Provider>
    </MapEditorStateContext.Provider>
  )
}

export function useMapEditorProvider() {
  const context = React.useContext(MapEditorStateContext)

  if (typeof context === undefined) {
    throw new Error('useMapEditorState must be used within a MapEditorProvider')
  }

  return context
}

export function useMapEditorUpdater() {
  const dispatch = React.useContext(MapEditorDispatchContext)

  if (typeof dispatch === undefined) {
    throw new Error('useMapEditorUpdater must be used within a MapEditorProvider')
  }

  return {
    editLayer: (layer: Layer) => dispatch({ type: Actions.editLayer, payload: layer }),

    setWizard: (wizard: MapEditorWizards | null) =>
      dispatch({ type: Actions.setWizard, payload: wizard }),

    cancelWizard: () => dispatch({ type: Actions.cancelWizard }),

    featureCreated: () => dispatch({ type: Actions.featureCreated }),

    updateFeature: (feature: Feature) =>
      dispatch({ type: Actions.updateFeature, payload: feature }),

    setConfirm: (confirmed: boolean) => dispatch({ type: Actions.setConfirm, payload: confirmed }),

    selectTool: (areaTool: AreaSelection) =>
      dispatch({ type: Actions.selectTool, payload: areaTool }),
  }
}

type MapEditorContextType = [State, ReturnType<typeof useMapEditorUpdater>]
export const useMapEditorContext = (): MapEditorContextType => [
  useMapEditorProvider(),
  useMapEditorUpdater(),
]
